/* The counter, in the dashboard (2026-09-18).
 *
 * THE FOUNDER'S RULING: one product, and the role decides what you see. The owner
 * has the counter as one screen among the rest; anyone else on the team signs in
 * and has only this. It is the same component counter.damga.qa shows as a kiosk
 * -- the same queue, the same enrolment and email read-back, the same words for
 * every refusal -- so there is one till to prove, not two.
 *
 * FULL SCREEN, NOT A PANEL IN THE SIDEBAR LAYOUT, and that is a decision against
 * the artboard's picture of it. The artboard draws the counter inside the
 * dashboard frame. The till, though, is built for a tablet read at arm's length in
 * its own dark register, and its tokens share their names with this dashboard's
 * (--paper, --ink, --seal): run inside the frame, one register would repaint the
 * other. So the till takes the screen while it is open, its register goes in with
 * it and comes out when it closes, and the owner has a way back. A cashier on a
 * till tablet sees nothing but the till, which is the condition the ruling set.
 */
import { componentCss, registerMerchantCss } from "@damga/ds";
import tokensBase from "@damga/ds/tokens.css?raw";
import tokensOnly from "@damga/ds/tokens-only.css?raw";
import { counterCss } from "@damga/counter/styles";
import { Till, type TillBusiness } from "@damga/counter/till";
import type { createClient } from "@damga/kit";
import { useLayoutEffect } from "react";

/* The till's whole stylesheet, as the kiosk loads it. `@import` lines are dropped:
 * in a string they would resolve against this page, and the two faces they load
 * are ones this dashboard already declares itself. */
const TILL_CSS = [
  tokensOnly,
  tokensBase.replace(/@import[^;]+;/g, ""),
  registerMerchantCss,
  componentCss,
  counterCss,
].join("\n");

export function CounterScreen({
  api,
  business,
  endLabel,
  onEnd,
  onSessionEnded,
}: {
  api: ReturnType<typeof createClient>;
  business: TillBusiness;
  endLabel: string;
  onEnd: () => void;
  onSessionEnded: () => void;
}) {
  // Before paint, so the dashboard's ground never flashes under the till.
  useLayoutEffect(() => {
    const style = document.createElement("style");
    style.dataset.till = "";
    style.textContent = TILL_CSS;
    document.head.append(style);
    document.documentElement.dataset.mode = "till";
    return () => {
      style.remove();
      delete document.documentElement.dataset.mode;
    };
  }, []);

  return (
    <Till api={api} business={business} onSessionEnded={onSessionEnded} endLabel={endLabel} onEnd={onEnd} />
  );
}
